import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getUserById, getPreferences } from "../utils/api";
import { Loader2 } from "lucide-react";

function calculateMatch(profile, prefs) {
  if (!prefs) return 0;
  const checks = [];

  // Age
  if (prefs.ageRange?.min != null && prefs.ageRange?.max != null && profile.dob) {
    const age = new Date().getFullYear() - new Date(profile.dob).getFullYear();
    checks.push(age >= prefs.ageRange.min && age <= prefs.ageRange.max);
  }

  // Gender
  if (prefs.lookingFor) {
    const wantMale = prefs.lookingFor.toLowerCase() === "groom";
    checks.push((wantMale && profile.gender === "Male") || (!wantMale && profile.gender === "Female"));
  }

  // Mother tongue
  if (prefs.motherTongue?.length) {
    checks.push(prefs.motherTongue.includes(profile.motherTongue));
  }

  // Education
  if (prefs.education?.length) {
    const edus = Array.isArray(profile.education) ? profile.education : [profile.education];
    checks.push(edus.some(e => prefs.education.includes(e)));
  }

  // Location fields
  ["country", "state", "city"].forEach(loc => {
    if (prefs[loc]) checks.push(profile[loc] === prefs[loc]);
  });

  const total =
    (prefs.ageRange ? 1 : 0) +
    (prefs.lookingFor ? 1 : 0) +
    (prefs.motherTongue?.length ? 1 : 0) +
    (prefs.education?.length ? 1 : 0) +
    (prefs.country ? 1 : 0) +
    (prefs.state ? 1 : 0) +
    (prefs.city ? 1 : 0);

  const matched = checks.filter(Boolean).length;
  return total > 0 ? Math.round((matched / total) * 100) : 0;
}

export default function MatriRecentUpload({ profiles = [], loading, hasSearched }) {
  const navigate = useNavigate();
  const [hasFounder, setHasFounder] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [annotated, setAnnotated] = useState([]);

  // ID that denotes an entrepreneur
  const ENTREPRENEUR_ID = "68515ca576ce3b5d818b5b08";

  // Load founder status
  useEffect(() => {
    (async () => {
      const uid = localStorage.getItem("matrimonyUserId");
      if (!uid) return;
      try {
        const user = await getUserById(uid);
        setHasFounder(!!user.founder_id?.founderId);
      } catch {}
    })();
  }, []);

  // Load preferences
  useEffect(() => {
    (async () => {
      try {
        const arr = await getPreferences();
        setPrefs(arr[0] || null);
      } catch {}
    })();
  }, []);

  // Annotate profiles with match% and optional tag
  useEffect(() => {
    if (!profiles) {
      setAnnotated([]);
      return;
    }
    const list = profiles.map(p => ({
      ...p,
      matchPercent: calculateMatch(p, prefs),
      tag: p.occupation === ENTREPRENEUR_ID ? "Entrepreneur" : undefined
    }));
    setAnnotated(list);
  }, [profiles, prefs]);

  // Filter out non-entrepreneurs if user has no founder
  const display = hasFounder
    ? annotated
    : annotated.filter(p => p.occupation === ENTREPRENEUR_ID);

  if (hasSearched && !loading && display.length === 0) {
    return (
      <div className="py-10 text-center text-gray-500">
        No profiles matched your search.
      </div>
    );
  }

  return (
    <div className="container py-10 px-4 mx-auto">
      {hasFounder && (
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-3xl font-bold">
            Recent{" "}
            <span className="bg-gradient-to-r from-[#E25B46] via-[#B9398E] to-[#E25B46] bg-clip-text text-transparent">
              Uploads
            </span>
          </h2>
          <button
            className="text-sm text-[#E7603C] underline"
            onClick={() => navigate("/matrimony/profiles/recent")}
          >
            View All
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {display.slice(0, 10).map(profile => {
          const age = profile.dob
            ? new Date().getFullYear() - new Date(profile.dob).getFullYear()
            : "—";
          const isPrivate = profile.visibility === "Private";

          return (
            <div
              key={profile._id}
              className={`bg-white rounded-3xl overflow-hidden shadow-lg relative ${
                isPrivate ? "pointer-events-none" : ""
              }`}
            >
              {/* Tag badge */}
              {profile.tag && (
                <span className="absolute top-2 right-2 bg-yellow-300 px-3 py-1 text-sm font-semibold rounded-full">
                  {profile.tag}
                </span>
              )}

              {/* Match badge */}
              <div className="absolute top-2 left-2 bg-white bg-opacity-80 px-2 py-1 text-xs font-semibold rounded">
                {profile.matchPercent}% Match
              </div>

              <img
                src={profile.images?.[0] || "/fallback.jpg"}
                alt={profile.fullName || "Profile Image"}
                className={`w-full h-[300px] object-cover ${
                  isPrivate ? "filter blur-md" : ""
                }`}
              />

              {isPrivate && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                  <span className="text-white px-4 py-2 rounded-full text-sm">
                    Private Profile
                  </span>
                </div>
              )}

              <div className="bg-gradient-to-t from-black to-transparent text-white p-4 absolute bottom-0 left-0 right-0">
                <h3 className="text-lg font-bold mb-1">
                  {profile.fullName || "—"}
                </h3>
                <div className="text-xs grid grid-cols-2 gap-2 px-2 py-1">
                  <span className="flex items-center gap-1">
                    <img src="/icon1.png" alt="age" className="w-4 h-4" />
                    {age} Years
                  </span>
                  <span className="flex items-center gap-1">
                    <img src="/icon2.png" alt="height" className="w-4 h-4" />
                    {profile.height || "—"}
                  </span>
                  <span className="flex items-center gap-1">
                    <img src="/icon3.png" alt="location" className="w-4 h-4" />
                    {profile.cityName || profile.address || "—"}
                  </span>
                  <span className="flex items-center gap-1">
                    <img src="/icon4.png" alt="gender" className="w-4 h-4" />
                    {profile.gender || "—"}
                  </span>
                </div>
                <button
                  className="mt-2 px-4 py-1.5 bg-gradient-to-r from-[#E7603C] to-[#B9309A] rounded-full text-xs disabled:opacity-50"
                  onClick={() =>
                    navigate(`/matrimony/profile-details/${profile._id}`)
                  }
                  disabled={isPrivate}
                >
                  View Profile
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {loading && (
        <div className="flex justify-center mt-8">
          <Loader2 className="w-6 h-6 animate-spin text-[#E7603C]" />
        </div>
      )}
    </div>
  );
}
